/**
 * In-app help articles — one per module key.
 *
 * Structure:
 *   {
 *     key:        sidebar module_key this article documents
 *     title:      page title
 *     audience:   one or more of ['agent','supervisor','admin'] — used for
 *                 the /help index filtering
 *     sections:   array of { heading, body[] }
 *       body items are { type: 'p' | 'list' | 'tip' | 'warn' | 'kbd', text|items }
 *   }
 *
 * Articles intentionally avoid screenshots so the doc stays portable. Add
 * inline screenshots later by importing them and referencing in `body`.
 */

export const ARTICLES = {
  // ────────────────────────────────────────────────────────────────
  // SOFTPHONE / WebRTC client
  // ────────────────────────────────────────────────────────────────
  softphone: {
    key: 'softphone',
    title: 'Softphone (WebRTC client)',
    audience: ['agent', 'supervisor', 'admin'],
    sections: [
      {
        heading: 'What it is',
        body: [
          { type: 'p', text:
            "The Softphone is your phone. It runs entirely in this browser tab — no separate app, no headset software to install. When you're logged in, it auto-registers to ContinuumCX and lets you make and receive calls." },
          { type: 'tip', text:
            'Keep this tab open. Closing it disconnects you from the queue.' },
        ],
      },
      {
        heading: 'Status bar (top of the card)',
        body: [
          { type: 'p', text: 'The status bar shows your current ACD state. Click the leftmost pill to switch between Not Ready and Ready.' },
          { type: 'list', items: [
            '○ Not Ready — you are NOT in the queue. Default after login.',
            '✓ Ready (green) — you will receive queue calls.',
            '⏸ AUX — paused with a reason (Lunch, Break, Meeting, Training, Admin, Other).',
            '📝 Wrap — post-call wrap-up; auto-flips back to Ready when the queue timer ends.',
            '🚪 Logout — removes you from every queue and disconnects your softphone.',
          ]},
          { type: 'tip', text: 'A supervisor can force-pause or force-logout you. If that happens you will see a notice in the status bar.' },
        ],
      },
      {
        heading: 'Receiving a call',
        body: [
          { type: 'list', items: [
            'Calls from queues with auto-answer enabled will pick up automatically — no click needed.',
            'For other inbound calls, click the green ✓ Answer button.',
            'A short "whisper" announcement may play to you before the customer is bridged in — telling you which queue the call came from.',
            'Once connected, your call timer starts and the call quality (QoS) panel can be opened for live metrics.',
          ]},
        ],
      },
      {
        heading: 'Making a call',
        body: [
          { type: 'list', items: [
            'Type the number in the dial input and click 📞 Dial.',
            'Use the Dialpad (☰) for DTMF tones once the call is connected.',
            'Click ↺ Redial to call the last dialed number.',
          ]},
        ],
      },
      {
        heading: 'In-call controls (bottom of the card — always visible)',
        body: [
          { type: 'list', items: [
            '🔇 Mute / 🔊 Unmute — toggles your microphone.',
            '⏸ Hold / ▶ Unhold — puts the customer on hold (music on hold plays).',
            '↗ Transfer — blind transfer to another extension or PSTN number.',
            '👥 Conference — start a 3-way call.',
            '📞 Hangup — ends the call.',
            '☰ Dialpad — shows the DTMF keypad.',
          ]},
        ],
      },
      {
        heading: 'QoS panel (📶 button, top-right of the card)',
        body: [
          { type: 'p', text: 'Shows live network quality of the current call, refreshing every 2 seconds.' },
          { type: 'list', items: [
            'MOS — overall quality score (Excellent / Good / Fair / Poor / Bad).',
            'Jitter — variation in packet timing. Under 30 ms is good.',
            'RTT — round-trip time. Under 200 ms is good.',
            'Packet loss — % of audio packets dropped. Under 0.5% is good.',
          ]},
          { type: 'p', text: 'Click ◂ Collapse to shrink the QoS card to a thin strip without dropping audio. Click the strip to expand again.' },
          { type: 'tip', text: 'If MOS drops to Fair or worse, screenshot the QoS panel and notify your supervisor — it helps diagnose call quality issues.' },
        ],
      },
      {
        heading: 'Settings',
        body: [
          { type: 'p', text: 'Click ⚙️ Settings to pick your microphone, speaker, ring device, and run an audio test.' },
        ],
      },
      {
        heading: 'Troubleshooting',
        body: [
          { type: 'list', items: [
            'No audio after answering — Settings → check selected mic/speaker; reload the tab.',
            'Calls do not ring you — make sure the status bar shows ✓ Ready.',
            'Sudden disconnect — your supervisor may have force-logged you out, or your laptop went to sleep. Sign in again.',
            'High jitter / poor MOS — switch to a wired network or move closer to your Wi-Fi access point.',
          ]},
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ACD — Wallboard
  // ────────────────────────────────────────────────────────────────
  acd_floor: {
    key: 'acd_floor',
    title: 'Wallboard',
    audience: ['supervisor', 'admin'],
    sections: [
      {
        heading: 'What it shows',
        body: [
          { type: 'p', text: 'Live overview of all portal-managed queues and agents. Refreshes every 5 seconds.' },
        ],
      },
      {
        heading: 'KPI cards',
        body: [
          { type: 'p', text: 'Two rows of cards across the top:' },
          { type: 'list', items: [
            'Row 1 — Calls in queue · Longest wait · SLA (overall) · Agents available',
            'Row 2 — Offered today · Answered today · Abandoned today · Occupancy',
          ]},
          { type: 'tip', text: 'Cards flash red when a metric crosses a threshold (e.g. longest wait > 30s or occupancy ≥ 85%).' },
        ],
      },
      {
        heading: 'Agent status pie',
        body: [
          { type: 'p', text: 'Visual breakdown of agents by current state: Ready, AUX, Wrap, On Call, Not Ready. Labels show count and percentage outside each slice.' },
        ],
      },
      {
        heading: 'Per-queue chart + table',
        body: [
          { type: 'p', text: 'Stacked bar chart shows Waiting / Done / Abandoned per queue. The table below has every queue with live counters, member counts, today’s totals and SLA.' },
          { type: 'p', text: 'Use the table filters to narrow by queue or DID.' },
        ],
      },
      {
        heading: 'Tenant filter (admins only)',
        body: [
          { type: 'p', text: 'Top-right Client dropdown limits everything on the page to one tenant’s queues. Pick "All tenants" to see global numbers. Tenant-bound supervisors don’t see this dropdown — their view is already scoped automatically.' },
        ],
      },
      {
        heading: 'TV mode',
        body: [
          { type: 'p', text: 'Click ⛶ Full Screen to use the Wallboard as a dashboard on a TV — chart sizes grow and the header text scales up. Press Esc to exit.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ACD — Queues
  // ────────────────────────────────────────────────────────────────
  acd_queues: {
    key: 'acd_queues',
    title: 'Queues — detail',
    audience: ['supervisor', 'admin'],
    sections: [
      {
        heading: 'What it shows',
        body: [
          { type: 'p', text: 'One card per portal-managed queue: callers currently waiting (with wait time), members (with state and queue stats), and per-queue counters.' },
        ],
      },
      {
        heading: 'Filtering',
        body: [
          { type: 'list', items: [
            'Queue — pick a specific queue to focus on.',
            'Agent — search for a member by extension or name.',
            'Skill — filter by skill tag if your queues use skills routing.',
            'DID — filter by direct inbound number.',
          ]},
        ],
      },
      {
        heading: 'Tenant filter (admins only)',
        body: [
          { type: 'p', text: 'Same as on the Wallboard — pick a client to narrow the queue list. Hidden for tenant-bound users.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ACD — Agent Personal
  // ────────────────────────────────────────────────────────────────
  acd_personal: {
    key: 'acd_personal',
    title: 'Agent Personal',
    audience: ['agent', 'supervisor', 'admin'],
    sections: [
      {
        heading: 'What it shows',
        body: [
          { type: 'p', text: 'Your own ACD view: current state, time-in-state, calls handled today, and the queues you are assigned to.' },
        ],
      },
      {
        heading: 'When to use it',
        body: [
          { type: 'list', items: [
            'See your handled-call count for the day.',
            'Track how long you have been in AUX or Wrap.',
            'Confirm which queues you are receiving calls from.',
          ]},
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ACD — Supervisor Tools
  // ────────────────────────────────────────────────────────────────
  acd_supervisor: {
    key: 'acd_supervisor',
    title: 'Supervisor Tools',
    audience: ['supervisor', 'admin'],
    sections: [
      {
        heading: 'Live agent list',
        body: [
          { type: 'p', text: 'Shows agents you can target with monitor/whisper/barge/force actions. Refreshes every 5 seconds. Tenant-scoped: client-supervisors only see agents in queues their tenant owns.' },
        ],
      },
      {
        heading: '🎧 Listen (silent monitor)',
        body: [
          { type: 'p', text: 'Lets you hear both sides of the agent’s current call. Neither party hears you. Useful for quality monitoring and coaching prep.' },
        ],
      },
      {
        heading: '💬 Whisper',
        body: [
          { type: 'p', text: 'Only the agent hears you — the customer keeps their normal conversation. Use it to coach a live call in real time.' },
        ],
      },
      {
        heading: '🤝 Barge',
        body: [
          { type: 'p', text: 'Both the agent and the customer hear you — a true three-way call. Use when you need to step in.' },
          { type: 'warn', text: 'Barge interrupts the call. Confirm the dialog before bargeing.' },
        ],
      },
      {
        heading: 'How spying works in this app',
        body: [
          { type: 'list', items: [
            'Clicking any of Listen / Whisper / Barge auto-switches you to the Softphone tab.',
            'Your softphone auto-answers the incoming spy call within ~1–2 seconds — no click required.',
            'To stop, click 📞 Hangup. The agent will not see anything change.',
          ]},
          { type: 'tip', text: 'You must have an extension assigned to your user (Admin → Agents). Otherwise spying returns "supervisor_has_no_extension".' },
        ],
      },
      {
        heading: '⏸ Force Pause / 🚪 Force Logout',
        body: [
          { type: 'p', text: 'Force Pause puts the agent in AUX with a reason you supply (e.g. "training"). Force Logout removes them from every queue and disconnects their softphone.' },
          { type: 'p', text: 'Both actions push a notice to the agent’s softphone via real-time SSE so they see the state flip immediately. Every action is audit-logged.' },
        ],
      },
      {
        heading: 'Broadcasts',
        body: [
          { type: 'p', text: 'Send a short notice to all agents (or a subset of queues). Pick a severity — Info, Warning, Major, Critical — and an optional title. Agents see it as a banner on their softphone and dismiss it; the dismissal is recorded.' },
        ],
      },
      {
        heading: 'Tenant filter (admins only)',
        body: [
          { type: 'p', text: 'Same as the other ACD views — limit the agent list to a specific client. Hidden for tenant-bound supervisors.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // RECORDINGS
  // ────────────────────────────────────────────────────────────────
  recordings_browser: {
    key: 'recordings_browser',
    title: 'Recordings',
    audience: ['supervisor', 'admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Browse, play, and download call recordings ingested from the active ACD. Recordings are tenant-scoped: you only see calls for queues your tenant owns.' },
        ],
      },
      {
        heading: 'Filtering',
        body: [
          { type: 'list', items: [
            'Date range — Today / This week / This month / Custom.',
            'Queue — narrow to one of your queues.',
            'Agent extension — see a specific agent’s calls.',
            'Direction — inbound / outbound / internal.',
          ]},
        ],
      },
      {
        heading: 'Playback + download',
        body: [
          { type: 'list', items: [
            'Click the ▶ button to stream the recording inline (no download).',
            'Click ⤓ Download for a local WAV/MP3 copy.',
            'Archived recordings (stored in Azure Blob) stream the same way — you do not have to know whether the file is local or archived.',
          ]},
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // REPORTS
  // ────────────────────────────────────────────────────────────────
  reports: {
    key: 'reports',
    title: 'Reports',
    audience: ['supervisor', 'admin'],
    sections: [
      {
        heading: 'Available reports',
        body: [
          { type: 'list', items: [
            'Agent productivity — calls handled (inbound / outbound / internal), talk time, AHT per agent.',
            'Queue performance — offered / answered / abandoned / missed / transferred / hold events per queue.',
            'ACD historical — the live ACD dashboard counters, back in time. Per-queue + daily trend + CSV/PDF.',
            'CDR drill-down — every call with filters; click the colored Disconnect pill for a full CEL trace.',
            'Trunks — Telnyx and other trunks: count, ASR, avg call duration.',
            'Recording coverage — voice + screen pair rate, per-agent coverage, orphan screen recordings.',
            'QA scorecards — per agent, per form, per evaluator. Includes CSV/PDF export.',
            'Outbound compliance — dial-precheck denials/allows per agent and per country.',
            'Storage & archive — bytes/GB stored per tenant/month/tier for billing.',
            'Transfer report — transfer counts per queue (blind + attended + conference).',
          ]},
        ],
      },
      {
        heading: 'Filters',
        body: [
          { type: 'p', text: 'Every report has a filter bar under the range selector. Available filters depend on the report:' },
          { type: 'list', items: [
            'Agent — narrow to one agent extension.',
            'Queue — narrow to one queue.',
            'Direction — inbound / outbound / internal (Agent productivity).',
            'DID / Disposition — CDR drill-down only.',
            'Form / Evaluator — QA sub-tabs.',
          ]},
          { type: 'tip', text: 'The DC filter was removed because reports UNION both DCs automatically; users don\'t need to choose.' },
        ],
      },
      {
        heading: 'Dual-DC data',
        body: [
          { type: 'p', text: 'CDR reports pull from BOTH datacenters and union the results. If one DC is temporarily unreachable, a ⚠ partial banner appears at the top of the table.' },
        ],
      },
      {
        heading: 'Time presets',
        body: [
          { type: 'list', items: [
            'Today / Yesterday / This week / This month / Last 7d / Last 30d',
            'Custom — pick a start and end date (max 90 days).',
          ]},
          { type: 'p', text: 'The "Today" preset auto-refreshes every 30 seconds. Historical ranges are on demand — click ↻ to refresh.' },
        ],
      },
      {
        heading: 'Disconnect direction (CDR drill-down)',
        body: [
          { type: 'p', text: 'Every row shows who disconnected first — Caller, Agent, or No connect. The label has "(inferred)" because this is the fast heuristic. Click it to open a side panel with the authoritative CEL trace: per-leg timestamps, full event ladder, and the exact channel that left the bridge first.' },
        ],
      },
      {
        heading: 'Export',
        body: [
          { type: 'list', items: [
            '⤓ CSV — download as CSV for spreadsheets.',
            '⤓ PDF — download as a landscape PDF with branded header.',
          ]},
        ],
      },
      {
        heading: 'Tenant scope',
        body: [
          { type: 'p', text: 'Client users see only their tenant’s queues and agents. Continuum employees see everything. Admins can use the tenant dropdown to narrow.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ADMIN modules
  // ────────────────────────────────────────────────────────────────
  admin_agents: {
    key: 'admin_agents',
    title: 'Admin → Agents',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Provision the agents who can log into ContinuumCX. Each agent gets an extension, queue assignments, optional skill levels, and (auto-generated) SIP credentials that the softphone fetches transparently.' },
        ],
      },
      {
        heading: 'Common tasks',
        body: [
          { type: 'list', items: [
            'Add an agent — fill in email, extension, queues; password is auto-generated and emailed via Resend.',
            'Edit — change queue assignments, skill level, recording flag, wrap time.',
            'CSV import — bulk-add. A diff preview shows what will change BEFORE you commit.',
            'Reset password — generates a new temp password (shown once in a modal).',
          ]},
        ],
      },
    ],
  },

  admin_queues: {
    key: 'admin_queues',
    title: 'Admin → Queues',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Create and edit portal-managed queues. Queue IDs must start with 6 (e.g. 60100, 69101) and are reserved for ContinuumCX — they do not collide with legacy Issabel queues.' },
        ],
      },
      {
        heading: 'Per-queue settings',
        body: [
          { type: 'list', items: [
            'Strategy — ringall, leastrecent, fewestcalls, random, etc.',
            'Pre-queue ringback — N seconds of ringback before the queue starts; then MOH while waiting.',
            'Member ring — how long each agent rings before the next is tried.',
            'Wrap-up time — post-call countdown the softphone runs.',
            'Max wait — when to fall back to the fallback destination.',
            'Auto-answer — agents pick up automatically.',
            'Whisper announcement — short TTS clip played to the agent only, just before the customer is bridged in.',
            'Tenant assignment — which client owns this queue.',
          ]},
        ],
      },
      {
        heading: 'Sync to LOU Issabel',
        body: [
          { type: 'p', text: 'Click ↻ Re-sync all to push every queue to the active ACD’s dialplan. Saves to DB are independent — they only take effect after sync.' },
        ],
      },
    ],
  },

  admin_announcements: {
    key: 'admin_announcements',
    title: 'Admin → Announcements',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Create text-to-speech announcements with Piper TTS (local — no external API). Categories: whisper, ivr_greeting, hold_prompt, generic.' },
        ],
      },
      {
        heading: 'Voices',
        body: [
          { type: 'list', items: [
            'en-us-lessac-medium — English female.',
            'es-carlfm-x-low — Spanish (Spain).',
          ]},
        ],
      },
      {
        heading: 'Per-queue whisper',
        body: [
          { type: 'p', text: 'After creating a whisper announcement, attach it to a queue in Admin → Queues. Agents in that queue will hear the announcement immediately before each call.' },
        ],
      },
    ],
  },

  admin_recording: {
    key: 'admin_recording',
    title: 'Admin → Recording',
    audience: ['admin'],
    sections: [
      {
        heading: 'Three sections',
        body: [
          { type: 'list', items: [
            'Buckets — register Azure Blob storage for archived recordings.',
            'Retention policies — how long to keep local recordings before evicting.',
            'Archive rules — which calls go to which bucket (by queue, age, priority).',
          ]},
        ],
      },
      {
        heading: 'Workers',
        body: [
          { type: 'list', items: [
            'Ingest — SSH into the ACD, pull new recordings, register them in the DB.',
            'Retention — evict expired local recordings.',
            'Archive — upload matching local recordings to their configured bucket.',
          ]},
          { type: 'p', text: 'Each worker has a "Run now" button for manual triggering; production runs are scheduled.' },
        ],
      },
    ],
  },

  admin_dids: {
    key: 'admin_dids',
    title: 'Admin → DIDs',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Manage inbound numbers (DIDs / TFNs) and what they route to: a queue, an agent extension, an IVR, voicemail, or a custom Asterisk context.' },
        ],
      },
      {
        heading: 'Sync',
        body: [
          { type: 'p', text: 'After editing, click sync to push the DID block into LOU Issabel’s dialplan. DID changes only take effect post-sync.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // SETTINGS
  // ────────────────────────────────────────────────────────────────
  settings_users: {
    key: 'settings_users',
    title: 'Settings → Users',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Create, edit, disable, and reset passwords for any user — admins, supervisors, agents, viewers. Tenant assignment puts the user under a client.' },
        ],
      },
      {
        heading: 'Password reset',
        body: [
          { type: 'p', text: 'Generates a new temp password, shown ONCE in a modal so you can copy it. Also emailed via Resend if configured. The user is forced to change it on next login.' },
        ],
      },
    ],
  },

  settings_clients: {
    key: 'settings_clients',
    title: 'Settings → Clients',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'Manage tenants (clients). Each tenant can have its own logo (shown in the sidebar footer for users of that tenant), a contact, and a status.' },
        ],
      },
    ],
  },

  settings_permissions: {
    key: 'settings_permissions',
    title: 'Settings → Permissions',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it does',
        body: [
          { type: 'p', text: 'A matrix of role × module. Toggle a cell to grant or remove access. Admin is always allowed everywhere (enforced in code) — toggles for admin are cosmetic.' },
        ],
      },
      {
        heading: 'Saving',
        body: [
          { type: 'p', text: 'Unsaved changes show an amber ring. Click Save to apply. Sidebar gating updates immediately for users on their next page load.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // ACCOUNT
  // ────────────────────────────────────────────────────────────────
  account: {
    key: 'account',
    title: 'Account',
    audience: ['agent', 'supervisor', 'admin'],
    sections: [
      {
        heading: 'What you can do',
        body: [
          { type: 'list', items: [
            'See your profile (email, role, tenant).',
            'Change your password.',
            'Toggle light / dark theme.',
            'Sign out.',
          ]},
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // NEW (2026-07-09 session)
  // ─────────────────────────────────────────────────────────────

  admin_failover: {
    key: 'admin_failover',
    title: 'Cluster Failover',
    audience: ['admin'],
    sections: [
      {
        heading: 'What it is',
        body: [
          { type: 'p', text:
            'Flip which ACD cluster (DEN or LOU) the platform treats as active. This affects where new calls are routed and which AMI events reports/dashboards read. Both clusters continue to receive events from Kamailio; only one is "active" for write operations.' },
          { type: 'warn', text:
            'This is a CX-layer flip only. It does NOT promote Postgres or reconfigure Kamailio. Do that separately as documented in FAILOVER-AND-DR.md.' },
        ],
      },
      {
        heading: 'When to use it',
        body: [
          { type: 'list', items: [
            'Planned maintenance on the currently-active DC.',
            'Emergency: the current active is unresponsive.',
            'Testing the standby DC end-to-end.',
          ]},
        ],
      },
      {
        heading: 'How to flip',
        body: [
          { type: 'list', items: [
            'Open Admin → Cluster Failover.',
            'Confirm the current active from the state card.',
            'Pick the target cluster (den or lou) and add a reason (e.g. "maintenance window").',
            'Click Flip. The response shows the new active state.',
          ]},
          { type: 'tip', text: 'Every flip writes a row to acd_cluster_events + audit_events. The Reports → Failover Timeline surfaces the history.' },
        ],
      },
    ],
  },

  qa_assignments: {
    key: 'qa_assignments',
    title: 'QA — Assign Evaluations',
    audience: ['admin', 'supervisor'],
    sections: [
      {
        heading: 'What it is',
        body: [
          { type: 'p', text:
            'Batch-create draft QA evaluations by matching criteria (queue, agent, date range, duration, direction, random sample) and assigning them to one or more evaluators. Each recording gets ONE draft per evaluator.' },
          { type: 'tip', text:
            'A "draft" evaluation IS the assignment. Once the evaluator opens it and submits, it becomes a real scored evaluation.' },
        ],
      },
      {
        heading: 'How to run it',
        body: [
          { type: 'list', items: [
            'Pick a form from Admin → QA Forms first.',
            'Set criteria: queues, agents, date range, min/max duration, direction, sample %, and max limit.',
            'Pick one or more evaluators from the user list.',
            'Optional: check Dry run to preview matched count + per-evaluator distribution.',
            'Click Assign. The response shows how many drafts were created and how many were skipped (deduped or no agent).',
          ]},
        ],
      },
      {
        heading: 'Evaluator flow',
        body: [
          { type: 'p', text: 'Evaluators go to QA → My Assignments and see draft rows. Click a row to open the recording + form; submit to score it.' },
        ],
      },
    ],
  },

  qa_reports: {
    key: 'qa_reports',
    title: 'QA Scorecards',
    audience: ['admin', 'supervisor'],
    sections: [
      {
        heading: 'Three sub-tabs',
        body: [
          { type: 'list', items: [
            'Per agent — per-agent count, avg / min / max score, first & last evaluation.',
            'Per form — usage and avg score per form + per-question analytics (which questions score worst).',
            'Per evaluator — evaluator activity + STDDEV_POP (calibration hint).',
          ]},
        ],
      },
      {
        heading: 'Filters',
        body: [
          { type: 'list', items: [
            'Agent — narrow the per-agent tab.',
            'Form — restrict all tabs to one form.',
            'Evaluator — filter the per-agent tab by evaluator (useful for "how does this evaluator score").',
          ]},
        ],
      },
      {
        heading: 'Export',
        body: [
          { type: 'p', text: 'The active sub-tab exports to CSV / PDF via the buttons in the top right.' },
        ],
      },
    ],
  },
};

/**
 * Return the article for a module_key, or null if there isn't one.
 */
export function getArticle(moduleKey) {
  return ARTICLES[moduleKey] || null;
}

/**
 * List all articles visible to a given role.
 */
export function articlesForRole(role) {
  const r = String(role || '').toLowerCase();
  return Object.values(ARTICLES).filter((a) => a.audience.includes(r) || r === 'admin');
}
